// Team Icon Selection Modal Component

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Shuffle } from 'lucide-react';
import { TEAM_ICONS } from '@/lib/team-icons';

interface TeamIconModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentIcon: string;
  currentColor: string;
  onSelect: (iconKey: string) => void;
}

export function TeamIconModal({
  open,
  onOpenChange,
  currentIcon,
  currentColor,
  onSelect,
}: Readonly<TeamIconModalProps>) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredIcons = TEAM_ICONS.filter((icon) =>
    icon.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleRandomSelect = () => {
    const randomIndex = Math.floor(Math.random() * TEAM_ICONS.length);
    onSelect(TEAM_ICONS[randomIndex].name);
    onOpenChange(false);
  };

  const handleSelect = (iconKey: string) => {
    onSelect(iconKey);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Team Icon</DialogTitle>
          <DialogDescription>
            Choose an icon to represent your team
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 min-h-0 flex-1">
          {/* Search and Random Button */}
          <div className="flex gap-2 flex-shrink-0">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search icons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              onClick={handleRandomSelect}
              className="gap-2"
            >
              <Shuffle className="h-4 w-4" />
              Random
            </Button>
          </div>

          {/* Icon Grid */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="grid grid-cols-6 gap-3 pr-4">
              {filteredIcons.map((iconData) => {
                const Icon = iconData.icon;
                const isSelected = currentIcon === iconData.name;
                return (
                  <button
                    key={iconData.name}
                    type="button"
                    onClick={() => handleSelect(iconData.name)}
                    className={`
                      group relative h-20 rounded-lg flex flex-col items-center justify-center gap-1 border-2 transition-all
                      ${
                        isSelected
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50 hover:bg-accent'
                      }
                    `}
                    style={
                      isSelected
                        ? {
                            backgroundColor: `${currentColor}20`,
                            color: currentColor,
                            borderColor: currentColor,
                          }
                        : {}
                    }
                    title={iconData.name}
                  >
                    <Icon className="h-6 w-6" />
                    <span className="text-[10px] font-medium truncate max-w-full px-1">
                      {iconData.name}
                    </span>
                  </button>
                );
              })}
            </div>
            {filteredIcons.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No icons found matching "{searchQuery}"
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
